import type { TicketType } from '@/types';

/**
 * Validates ticket quantities for a multi-tenant (bulk quantity) purchase flow.
 *
 * @returns Error message for showError, or null if valid
 */
export function validateMultiTenantTicketSelections(
    availableTickets: TicketType[],
    quantities: Record<string, number>
): string | null {
    if (availableTickets.length === 0) {
        return null;
    }

    let total = 0;
    for (const ticket of availableTickets) {
        const qty = quantities[ticket.id] ?? 0;
        if (!Number.isInteger(qty) || qty < 0) {
            return 'Ticket quantities must be whole numbers of zero or more';
        }
        if (typeof ticket.totalAvailable === 'number' && qty > ticket.totalAvailable) {
            return `Only ${ticket.totalAvailable} of "${ticket.name}" tickets are available`;
        }
        total += qty;
    }

    if (total === 0) {
        return 'Please select a quantity for at least one ticket type';
    }

    return null;
}

/**
 * Writes responses.Tickets for a multi-tenant (bulk quantity) purchase.
 * Deliberately does not set responses.TicketCourses — multi-tenant tickets have no meal choices.
 */
export function mergeMultiTenantTicketsIntoResponses(
    responses: Record<string, any>,
    availableTickets: TicketType[],
    quantities: Record<string, number>
): void {
    responses.Tickets = availableTickets
        .filter((t) => (quantities[t.id] ?? 0) > 0)
        .map((t) => ({
            id: t.id,
            name: t.name,
            price: t.price,
            qty: quantities[t.id],
        }));
}

/** Sum of price * qty across all selected ticket types. */
export function computeMultiTenantCartTotal(
    availableTickets: TicketType[],
    quantities: Record<string, number>
): number {
    return availableTickets.reduce((sum, t) => sum + t.price * (quantities[t.id] ?? 0), 0);
}

/** Total number of tickets (headcount) selected across all ticket types. */
export function computeMultiTenantTotalQuantity(quantities: Record<string, number>): number {
    return Object.values(quantities).reduce((sum, qty) => sum + (qty || 0), 0);
}
